import { IGrainWithProfiles } from 'src/models';

export const generateReadableDatasetName = (
  grainId: string,
  grainProfiles?: IGrainWithProfiles[],
  userInput?: string,
  jobId?: string
): string => {
  // Get current date in YYYY-MM-DD format
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0]; // Format: 2025-08-28
  const grainName = getGrainNameFromId(grainId, grainProfiles) || `grain_${grainId}`;
  const cleanGrainName = grainName.toLowerCase().replace(/[^a-z0-9]/g, '_');

  if (jobId) {
    // For job-based datasets: dataset_jobId_<grain_name>_<date>_<jobId>
    return `dataset_jobId_${cleanGrainName}_${dateStr}_${jobId}`;
  } else {
    // For regular datasets: dataset_<grain_name>_<date>_<user_input>
    const userSuffix = userInput ? userInput.toLowerCase().replace(/[^a-z0-9]/g, '_') : 'manual_entry';
    return `dataset_${cleanGrainName}_${dateStr}_${userSuffix}`;
  }
};

const getGrainNameFromId = (grainId: string, grainProfiles?: IGrainWithProfiles[]): string | null => {
  if (!grainProfiles || grainProfiles.length === 0) {
    return null;
  }
  
  const grain = grainProfiles.find(g => g.grainId === grainId);
  return grain ? grain.name : null;
};

export const isReadableDatasetName = (datasetName: string): boolean => {
  // Check for job-based dataset format: dataset_jobId_<grain_name>_<date>_<jobId>
  const jobBasedPattern = /^dataset_jobId_[a-z0-9_]+_\d{4}-\d{2}-\d{2}_[a-z0-9\-]+$/i;
  
  // Check for regular dataset format: dataset_<grain_name>_<date>_<user_input>
  const regularPattern = /^dataset_[a-z0-9_]+_\d{4}-\d{2}-\d{2}_[a-z0-9_]+$/i;
  
  return jobBasedPattern.test(datasetName) || regularPattern.test(datasetName);
};

export const parseDatasetName = (datasetName: string): {
  isJobBased: boolean;
  grainName: string;
  date: string;
  userInput?: string;
  jobId?: string;
} | null => {
  const jobBasedMatch = datasetName.match(/^dataset_jobId_([a-z0-9_]+)_(\d{4}-\d{2}-\d{2})_([a-z0-9\-]+)$/i);
  if (jobBasedMatch) {
    return {
      isJobBased: true,
      grainName: jobBasedMatch[1].replace(/_/g, ' '),
      date: jobBasedMatch[2],
      jobId: jobBasedMatch[3],
    };
  }

  const regularMatch = datasetName.match(/^dataset_([a-z0-9_]+)_(\d{4}-\d{2}-\d{2})_([a-z0-9_]+)$/i);
  if (regularMatch) {
    return {
      isJobBased: false,
      grainName: regularMatch[1].replace(/_/g, ' '),
      date: regularMatch[2],
      userInput: regularMatch[3].replace(/_/g, ' '),
    };
  }
  
  return null;
};
